/**
 * Guía: Organizaciones.
 *
 * Contenido derivado del capítulo **14** del manual: §0 (los dos usos de la
 * entidad y qué distingue a un evento) y §1 (presentación en el home,
 * vinculación de sucursales y compatibilidad de tipo de servicio).
 *
 * La distinción del §0 es toda la guía: la misma entidad sirve para una
 * agrupación permanente y para un evento con fechas, comisión propia y
 * platos inscritos. Confundirlas lleva a montar un festival como si fuera un
 * centro comercial.
 */

import type { TourFlow } from '../types';

const ORGS_PATH = '/organizations';

export const c14Organizaciones: TourFlow = {
  id: 'admin.organizations',
  chapter: '14',
  title: 'Organizaciones',
  summary:
    'Agrupar sucursales de comercios distintos bajo una etiqueta: cuándo es permanente y cuándo es un evento.',
  href: ORGS_PATH,
  requiresCountry: true,
  estimatedMin: 3,
  steps: [
    {
      id: 'intro',
      title: 'Una etiqueta que agrupa sucursales de comercios distintos',
      body:
        'Un centro comercial, una plaza de comidas, un festival. Es una entidad de ' +
        'presentación: define cómo se ve esa etiqueta en el inicio de la app y qué ' +
        'sucursales aparecen dentro. No vende ni cobra — los pedidos siguen siendo de la ' +
        'sucursal.',
      route: ORGS_PATH,
    },
    {
      id: 'two-uses',
      title: 'Permanente o evento: no es lo mismo',
      body:
        'Una agrupación permanente —«Centro Comercial Mayorca»— no caduca y no lleva nada ' +
        'más. Un evento —«Burger Master»— tiene fechas de inicio y fin, comisión propia y ' +
        'platos inscritos por sucursal. Elegir mal al crearla obliga a rehacerla.',
      target: 'admin.organizations.pageTabs',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      // Lo que ve un agente de soporte en lugar de los tres pasos que
      // enseñan a modificar: él lee las organizaciones para responder «no me
      // aplicó el descuento de mi convenio», pero atar o soltar sucursales
      // es una decisión comercial y el servidor tampoco se lo permite.
      id: 'read-only',
      title: 'Aquí sólo consultas',
      body:
        'Puedes abrir cualquier organización y ver qué sucursales la componen — que es lo ' +
        'que responde «pertenezco al convenio y no me aplicó el descuento». Cambiar quién ' +
        'participa lo hacen marketing, ventas o tu líder.',
      roles: ['SUPPORT_AGENT'],
    },
    {
      id: 'linking-stores',
      title: 'Vincular sucursales, no comercios',
      body:
        'Se vinculan sucursales concretas, y por eso una cadena puede estar dentro con un ' +
        'local y fuera con otro. Al vincular se comprueba que el tipo de servicio de la ' +
        'sucursal encaje con lo que la organización espera.',
      target: 'admin.organizations.table',
      placement: 'top',
      optionalTarget: true,
      roles: ['SUPER_ADMIN', 'MARKETING', 'SUPPORT_LEAD', 'SALES_MANAGER'],
    },
    {
      id: 'unlink-costs',
      title: 'Desvincular de un evento borra sus platos inscritos',
      body:
        'No es sólo quitarla de la lista: se pierde qué platos tenía inscritos en ese ' +
        'evento y con qué precio. Volver a vincularla no los devuelve, hay que inscribirlos ' +
        'otra vez.',
      roles: ['SUPER_ADMIN', 'MARKETING', 'SUPPORT_LEAD', 'SALES_MANAGER'],
    },
    {
      id: 'presentation',
      title: 'Cómo se ve en la app se decide aquí',
      body:
        'Tamaño de la ficha en la cuadrícula, colores, título e imágenes. Es lo que el ' +
        'cliente ve antes de entrar, así que una organización sin imágenes aparece, pero ' +
        'aparece mal.',
      roles: ['SUPER_ADMIN', 'MARKETING', 'SUPPORT_LEAD', 'SALES_MANAGER'],
    },
    {
      id: 'filters',
      title: 'Buscar y acotar por estado',
      body:
        'El listado está acotado al país seleccionado. Si una organización no aparece, ' +
        'comprueba el país antes de darla por perdida.',
      target: 'admin.organizations.filters',
      placement: 'bottom',
      optionalTarget: true,
    },
  ],
};
